//================================================================
//  Component: Review and Negotiate Tab
//================================================================

//  Purpose: This contains the review and negotiate information for a commercial task

//  Properties:
//    - task = {An object, contains the commercial task details}

//  Example:
//    <ReviewAndNegotiateTab
//      task={task}
//    ></ReviewAndNegotiateTab>    

// ==============================================================

// Libraries
import React, { useReducer, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Contexts
import { GetUser } from '../../../../Library/GlobalContexts';
import { SetToast } from '../../../../Library/GlobalContexts';

// Components
import AddSecondRoundApprovalPane from './AddSecondRoundApprovalPane';
import ViewApproverPane from './ViewApproverPane';
import CancelApprovalModal from '../Modals/CancelApprovalModal';
import Tooltip from '../../../../Components/Tooltip/Tooltip';
import CommentsThread from './CommentsThread';
import AddQuotePane from './AddQuotePane';
import EditQuotePane from './EditQuotePane';
import SubmitQuoteForReviewPane from './SubmitQuoteForReviewPane';
import StatusLabel from '../../../../Components/StatusLabel/StatusLabel';
import LiveField from '../../../../Components/LiveField/LiveField';
import KebabDropdown from '../../../../Components/KebabDropdown/KebabDropdown';

// Functions
import ConvertDate from '../../../../Library/ConvertDate';
import QueryListener from '../../../../Library/QueryListener';
import WriteDocument from '../../../../Library/WriteDocument';
import GetCollection from '../../../../Library/GetCollection';
import CreateRequest from '../../../../Library/CreateRequest';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';
import Add from '../../../../Components/Images/Icon_Add_Teal.svg';
import AddDisabled from '../../../../Components/Images/Icon_Add_Grey.svg';
import CancelDisabled from '../../../../Components/Images/Icon_CancelDisabled_Grey.svg';
import Info from '../../../../Components/Images/Icon_Info_Blue.svg';
import InfoRequired from '../../../../Components/Images/Icon_InfoRequired_Blue.svg';
import ExternalLink from '../../../../Components/Images/Icon_ExternalLink_Blue.svg';
import File from '../../../../Components/Images/Icon_File_Black.svg';

// CSS
import '../../Task.css'

export default function ReviewAndNegotiateTab({
    task
}) {
    //------------------------------------------------------
    //  useContexts and React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);
    const navigate = useNavigate();

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to toggle the visibility of the add approver pane
    const [addSecondRoundApprovalPaneOpen, setAddSecondRoundApprovalPaneOpen] = useState(false);

    // Used to toggle the visibility of the view approver pane and save the current approval
    const [viewPaneApprover, setViewPaneApprover] = useState(undefined);

    // Used to toggle the visibility of the cancel approval modal and save the current approval
    const [cancelModalApprover, setCancelModalApprover] = useState(false);

    // Used to store approval data
    const [cancelApprovalData, setCancelApprovalData] = useState(undefined);

    // Used to store the visibility of the add quote pane
    const [addQuotePaneOpen, setAddQuotePaneOpen] = useState(false);

    // Used to store the visibility of the edit quote pane
    const [editQuotePaneOpen, setEditQuotePaneOpen] = useState(false);

    // Used to store the visibility of the submit quote for review pane
    const [submitForReviewPaneOpen, setSubmitForReviewPaneOpen] = useState(false);

    // Used to store the selected Quote Line Item
    const [selectedQuoteLineItemIndex, setSelectedQuoteLineItemIndex] = useState(null);

    // Used to toggle of the execution required checkbox
    const [isExecutionNotRequired, setIsExecutionNotRequired] = useState(task?.executionnotrequired ?? false);

    // Used to store the details of agreement that has been published to Inventory
    const [agreementDetails, setAgreementDetails] = useState(undefined);

    // Used to determine if the agreement end date is set to null
    const [noEndDate, setNoEndDate] = useState(!task?.agreement?.agreementenddate === null);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded
    const [taskSection, setTaskSection] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'firstRoundApprovalsCollapsed': true,
            'finalApprovalsCollapsed': true,
            'secondRoundApprovalsCollapsed': true,
            'quotesCollapsed': true,
            'agreementInformationCollapsed': true,
            'commentsCollapsed': true,
        }
    );

    // Used to store the first round approval, second approval and quote review requests
    const [approvalData, setApprovalData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'firstRoundApprovals': [],
            'initialAndOtherApprovals': [],
            'finalApprovals': [],
            'secondRoundApprovals': [],
            'quoteReviewTasks': [],
            'demandManagementTask': undefined,
            'currentQuoteReviewTask': undefined,
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle the Execution Required checkbox
    function handleExecutionNotRequired(value) {

        setIsExecutionNotRequired(value);

        // Update the document
        const updatedDoc = {
            'executionnotrequired': value
        };

        WriteDocument('requests', task?.requestid, updatedDoc, true)
            .catch((error) => {

                console.log('error', error);

                setToast({
                    'type': 'error',
                    'message': 'Failed to bypass Execution stage',
                });

                // Set the value back to the original value
                setIsExecutionNotRequired(!value);


            });

    }

    // Function used to handle the Agreement End Date change
    // - If the value is null, set the noEndDate state to true
    function handleAgreementEndDateChange(check) {

        const value = check ? null : task?.agreement?.agreementenddate;

        setNoEndDate(check);

        // Update the document
        const updatedDoc = {
            'agreement': {
                'agreementenddate': value
            }
        };

        WriteDocument('requests', task?.requestid, updatedDoc, true)
            .catch((error) => {

                console.log('error', error);

                setToast({
                    'type': 'error',
                    'message': 'Failed to update Agreement End Date',
                });

            });

    }

    // Function used to check if the user can proceed to the next stage. Returns a list of tasks that are blocking the proceeding.
    // Will return empty array if tasks are fulfilled.
    function allowProceedToNextStageButton() {

        const blockersList = [];

        // Check all pending approvals
        const pendingFirstRoundApprovals = approvalData?.firstRoundApprovals?.filter((item) => item?.status === 'In Progress');
        const pendingSecondRoundApprovals = approvalData?.secondRoundApprovals?.filter((item) => item?.status === 'In Progress');
        const pendingQuotes = approvalData?.quoteReviewTasks?.filter((item) => item?.status === 'For Review');
        const pendingFinalApprovals = approvalData?.finalApprovals?.filter((item) => item?.status !== 'Completed')

        // If in 'Review and Negotiate' stage, allow proceeding
        if (task?.stage !== 'Review and Negotiate') blockersList.push('Task stage not in "Review and Negotiate"');

        // If this is a demand commercial request AND pending quotes exist AND final approvals have not been sent, prevent proceeding
        if (task?.requestorigin === 'Demand' && pendingQuotes?.length > 0 && (!approvalData?.demandManagementTask?.sentfinalapprovals || approvalData?.demandManagementTask?.sentfinalapprovals === undefined)) blockersList.push('Final Approvals have not been sent');

        // Check second round approvals (supplementary approvals)
        if (approvalData?.secondRoundApprovals?.filter((item) => item?.status === 'In Progress')?.length > 0) blockersList.push('Supplementary Approvals not complete');

        // Check first round approvals
        if (pendingFirstRoundApprovals?.length > 0) blockersList.push('Mandatory Approvals not complete');

        // Check second round approvals
        if (pendingSecondRoundApprovals?.length > 0) blockersList.push('Supplementary Approvals not complete');

        // Check final approvals only if there is a quote
        if (approvalData?.quoteReviewTasks.length > 0 && pendingFinalApprovals?.length > 0) blockersList.push('Final Approvals not complete');

        // Check quotes
        if (pendingQuotes?.length > 0) blockersList.push('Quote is still in review');

        // If status is already Completed, prevent proceeding 
        if (task?.status === 'Completed') blockersList.push('Already Completed');

        // ---- Check Agreement Information Fields ---- //

        // Check agreement type
        const agreementType = task?.agreement?.agreementtype === undefined ? task?.agreementtype : task?.agreement?.agreementtype;

        if (!agreementType) blockersList.push('Agreement Information Form: Agreement Type not set');

        // Check vendor name
        const vendorName = task?.agreement?.vendor?.vendorname === undefined || task?.agreement?.vendor?.vendorname === '' ? task?.vendor?.vendorname : task?.agreement?.vendor?.vendorname;
        if (!vendorName) blockersList.push('Agreement Information Form: Vendor name not set');

        // Check execution method
        if (!task?.agreement?.executionmethod) blockersList.push('Agreement Information Form: Execution method not set');

        // Check Lendlease entity
        if (!task?.agreement?.lendleaseentity) blockersList.push('Agreement Information Form: Lendlease entity not set');

        // Check agreement start date
        if (!task?.agreement?.agreementstartdate) blockersList.push('Agreement Information Form: Agreement start date not set');

        // Check agreement end date
        if (!task?.agreement?.agreementenddate && !noEndDate) blockersList.push('Agreement Information Form: Agreement end date not set');

        // Check auto renewals
        if (!task?.agreement?.autorenewals && (!['Establish NDA', 'Assign/Novate Agreement', 'Terminate Existing Agreement'].includes(task?.requesttype))) blockersList.push('Agreement Information Form: Auto renewals not set');

        // If auto renewals is Yes, check notice period
        if (task?.agreement?.autorenewals === 'Yes' && !task?.agreement?.noticeperiodforautorenewaltermination) blockersList.push('Agreement Information Form: Auto renewals not set');

        // Check extension agreement
        if (!task?.agreement?.extensionagreement && (!['Establish NDA', 'Assign/Novate Agreement', 'Terminate Existing Agreement'].includes(task?.requesttype))) blockersList.push('Agreement Information Form: Agreement extension not set');

        // If extension agreement is Yes, check notice period
        if (task?.agreement?.extensionagreement === 'Yes' && !task?.agreement?.noticeperiodforextension) blockersList.push('Agreement Information Form: Agreement extension not set');

        // Check document storage link
        if (!task?.agreement?.documentstoragelink) blockersList.push('Agreement Information Form: Document storage link not set');

        // Check hard savings
        if (!task?.agreement?.hardsavings && (!['Establish NDA', 'Assign/Novate Agreement', 'Terminate Existing Agreement'].includes(task?.requesttype))) blockersList.push('Agreement Information Form: Hard Savings not set');

        // Check soft savings
        if (!task?.agreement?.softsavings && (!['Establish NDA', 'Assign/Novate Agreement', 'Terminate Existing Agreement'].includes(task?.requesttype))) blockersList.push('Agreement Information Form: Soft Savings not set');

        // Return the list of blocked items
        return blockersList
    }

    // Function used to proceed to 'Raise PO' stage
    function moveToNextStage() {

        //=================================================
        //  Add Agreement Document in Firestore
        //=================================================

        const docPromises = [];

        // Create a searcharray for lookup fields
        const searchArray = [];
        let previousValue = '';
        [...task?.poanumber]?.forEach((value, index) => {

            // Skip the first character
            if (index === 0) {

                previousValue = value;
                return searchArray.push(value.toLowerCase());

            }

            // Create an array of each character
            searchArray.push(`${previousValue}${value}`.toLowerCase());
            previousValue = `${previousValue}${value}`.toLowerCase();

        });

        const agreementDoc = {
            'agreementid': task?.poanumber,
            'linkedrequests': {},
            'poanumber': task?.poanumber,
            'searcharray': searchArray,
            'createddate': new Date(),
            'createdby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'shortdescription': task?.shortdescription,
            'status': 'Draft',
            'expirydate': null,

            'commercialscope': task?.commercialscope,
            'parentcontract': task?.agreement?.parentcontract === undefined ? {} : task?.agreement?.parentcontract,
            'sponsorname': task?.sponsorname,
            'buysellagreement': task?.buysellagreement,
            'agreementtype': task?.agreement?.agreementtype === undefined ? task?.agreementtype : task?.agreement?.agreementtype,
            'agreementstartdate': task?.agreement?.agreementstartdate,
            'agreementenddate': task?.agreement?.agreementenddate,
            'executionmethod': task?.agreement?.executionmethod,
            'executiondate': task?.agreement?.executiondate,
            'lendleaseentity': task?.agreement?.lendleaseentity,
            'vendor': {
                'vendorname': task?.agreement?.vendor?.vendorname === undefined || task?.agreement?.vendor?.vendorname === '' ? task?.vendor?.vendorname : task?.agreement?.vendor?.vendorname,
                'vendorid': task?.agreement?.vendor?.vendorid === undefined || task?.agreement?.vendor?.vendorid === '' ? task?.vendor?.vendorid : task?.agreement?.vendor?.vendorid,
                'tradingas': task?.agreement?.vendor?.tradingas === undefined || task?.agreement?.vendor?.tradingas === '' ? task?.vendor?.tradingas : task?.agreement?.vendor?.tradingas,
            },
            'currency': task?.agreement?.currency === undefined || task?.agreement?.currency === '' ? task?.currency : task?.agreement?.currency,
            'totalagreementamount': task?.agreement?.totalagreementamount,
            'estannualcontractvalue': task?.agreement?.estannualcontractvalue === undefined ? task?.estannualcontractvalue : task?.agreement?.estannualcontractvalue,
            'esttotalcontractvalue': task?.agreement?.esttotalcontractvalue === undefined ? task?.esttotalcontractvalue : task?.agreement?.esttotalcontractvalue,
            'autorenewals': task?.agreement?.autorenewals,
            'noticeperiodforautorenewaltermination': task?.agreement?.noticeperiodforautorenewaltermination,
            'extensionagreement': task?.agreement?.extensionagreement,
            'noticeperiodforextension': task?.agreement?.noticeperiodforextension,
            'signedcontracts': task?.signedcontracts === undefined ? [] : task?.signedcontracts,
            'documentstoragelink': task?.agreement?.documentstoragelink,
            'hardsavings': task?.agreement?.hardsavings === '' || task?.agreement?.hardsavings === undefined ? '' : task?.agreement?.hardsavings,
            'softsavings': task?.agreement?.softsavings === '' || task?.agreement?.softsavings === undefined ? '' : task?.agreement?.softsavings,
            'ponumber': '',
            'polineitems': []
        }

        docPromises.push(
            WriteDocument('agreements', task?.poanumber, agreementDoc, true)
        );

        //  Update document in Firestore
        const updatedDoc = {
            stage: isExecutionNotRequired ? 'Raise PO' : 'Execute',
            'agreement': {
                'agreementid': task?.poanumber
            },
            lastmodifiedby: {
                email: getUser.emailaddress,
                givenname: getUser?.givenname,
                surname: getUser?.surname
            },
            lastmodifieddate: new Date()
        };

        docPromises.push(
            WriteDocument('requests', task?.requestid, updatedDoc, true)
        );

        Promise.all(docPromises)
            .then(() => {

                // Navigate to the next stage
                navigate(`/${window.location.pathname.split('/')[1]}/${task?.requestid}?view=${isExecutionNotRequired ? 'raisepo' : 'execute'}`);

                // Scroll to the top of the page
                const body = document.querySelector('.PageComponent-Container');

                body.scrollIntoView({
                    behavior: 'smooth'
                }, 300);

            })
            .catch((error) => {

                console.log('error', error);
                setTaskSection('Failed to close task, refresh and try again!');

            });

    }

    // Handle edit button click and set the selected quote line item
    const handleQuoteLineItemEdit = (quoteLineItemIndex) => {

        // Set the selected Quote Line Item Index
        setSelectedQuoteLineItemIndex(quoteLineItemIndex);

        // Open the Edit Quote Line Item Pane
        setEditQuotePaneOpen(true);

    };

    // Handle delete button click and set the selected quote line item
    const handleQuoteLineItemDelete = (quoteLineItemIndex, quoteTotal) => {

        // Remove quote line item in Quote Review document
        const updatedQuoteLineItems = approvalData?.currentQuoteReviewTask?.quote.filter((item, index) => index !== quoteLineItemIndex);

        // Update the total agreement amount in the current commercial request
        const updatedCommercialDoc = {
            'agreement': {
                'totalagreementamount': updatedQuoteLineItems?.length === 0 ? '' : task?.agreement?.totalagreementamount - quoteTotal,
                'currency': updatedQuoteLineItems?.length === 0 ? '' : task?.agreement?.currency
            },
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            }
        }

        // ----------------------------------------------------------
        //  If status is 'Rejected' > Create new quote review request
        // ----------------------------------------------------------

        if (approvalData?.currentQuoteReviewTask?.status === 'Rejected') {

            let finalPromises = [];

            return CreateRequest()
            .then((requestIds) => {

                // Create new quote review task based on the current rejected quote totals (only update relevant fields)
                approvalData.currentQuoteReviewTask.requestid = requestIds[0];
                approvalData.currentQuoteReviewTask.status = 'Draft';
                approvalData.currentQuoteReviewTask.activityfeedvisible = false; // This will change to true once the status is 'For Review'
                approvalData.currentQuoteReviewTask.taskdescription = '';
                approvalData.currentQuoteReviewTask.rejectionreason = '';
                approvalData.currentQuoteReviewTask.assigneddate = new Date();
                approvalData.currentQuoteReviewTask.createddate = new Date();
                approvalData.currentQuoteReviewTask.createdby = {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                };
                approvalData.currentQuoteReviewTask.activityfeed = [
                    {
                        'activitydate': new Date(),
                        'actionedby': {
                            'email': getUser.emailaddress,
                            'givenname': getUser.givenname,
                            'surname': getUser.surname,
                        },
                        'action': 'created',
                        'comments': '',
                    },
                    {
                        'activitydate': new Date(Date.now() + 1000),
                        'actionedby': {
                            'email': approvalData.currentQuoteReviewTask?.assignedto?.email,
                            'givenname': approvalData.currentQuoteReviewTask?.assignedto?.givenname,
                            'surname': approvalData.currentQuoteReviewTask.assignedto?.surname
                        },
                        'action': 'assigned',
                        'comments': '',
                    }
                ];
                approvalData.currentQuoteReviewTask.comment = '';
                approvalData.currentQuoteReviewTask.attachedfiles = [];
                approvalData.currentQuoteReviewTask.quote = updatedQuoteLineItems;

                // Create new quote
                finalPromises.push(
                    WriteDocument('requests', requestIds[0], approvalData?.currentQuoteReviewTask, true)
                );

                // Update total agreement amount and currency
                finalPromises.push(
                    WriteDocument('requests', task?.requestid, updatedCommercialDoc, true)
                );

                return Promise.all(finalPromises);

            })
            .catch((error) => {

                console.log('error', error);
                setToast({
                    'type': 'error',
                    'message': 'Failed to delete quote line item',
                });

            })

        }

        // ----------------------------------------------------------
        //  If status is 'Rejected' > Create new quote review request
        // ----------------------------------------------------------

        else {

            let finalPromises = [];

            // Update the document
            const updatedQuoteDoc = {
                'quote': updatedQuoteLineItems,
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
            };

            finalPromises.push(
                WriteDocument('requests', approvalData?.currentQuoteReviewTask?.requestid, updatedQuoteDoc, true)
            );

            // Update total agreement amount and currency on the commercial request
            finalPromises.push(
                WriteDocument('requests', task?.requestid, updatedCommercialDoc, true)
            );

            Promise.all(finalPromises)
            .catch((error) => {

                console.log('error', error);
                setToast({
                    'type': 'error',
                    'message': 'Failed to delete quote line item',
                });

            })

        }

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // - Get mandatory, final and supplementary round approvals and quotes
    useEffect(() => {

        if (task === undefined) return;
        if (task?.parentrequestid === undefined) return;

        function onLoadChange(document) {

            const demandManagementTask = document.find((doc) => doc?.tasktype === 'Digital Request');
            const quoteReviewTasks = document.filter((doc) => doc?.tasktype === 'Quote Review' && doc?.poanumber === task?.poanumber)
                                    .sort((a, b) => {
                                        return b.createddate - a.createddate; // Sort newest to oldest
                                    });
            let finalApprovals = document.filter((doc) => ['Finance Approval', 'LoA Approval', 'COO Approval', 'Final Approval'].includes(doc?.tasktype));                        

            // Check request origin and set the final approvals
            if (task?.requestorigin === 'Digital') {

                finalApprovals = document.filter((doc) => ['Budget Owner (LoA) Approval', 'Finance Manager Approval'].includes(doc?.tasktype));

            }

            // Get All Related Tasks
            setApprovalData({
                'firstRoundApprovals': document.filter((doc) => ['Initial Approval', 'Other Approval'].includes(doc?.tasktype)),
                'finalApprovals': finalApprovals,
                'secondRoundApprovals': document.filter((doc) => doc?.tasktype === 'Commercial Second Round Approval' && doc?.commercialrequestid === task?.requestid),
                'demandManagementTask': demandManagementTask,
                'quoteReviewTasks': quoteReviewTasks,
                'currentQuoteReviewTask': quoteReviewTasks.length === 0 ? { 'quote': [] } : quoteReviewTasks[0], 
            })

            if (task?.requestorigin === 'Digital') return;
            if (demandManagementTask?.sentfinalapprovals === true) return;

            // If Final Approvals NOT Sent > Get Current Final Approvers
            GetCollection('approvers')
                .then((approvers) => {

                    // ----------------------------------------------------------
                    // Get LoA Approver
                    // ----------------------------------------------------------

                    const loaRoleMapping = {
                        'Investment Management': 'Digital IDC',
                        'Investments': 'Digital IDC',
                        'Development': 'Digital IDC',
                        'Construction': 'Digital IDC',
                        'Defence': 'Digital IDC',
                        'Functions': 'Digital Functions',
                        'Digital': 'Technology',
                    };

                    // Mapped LoA approver role for the user
                    const loaRole = loaRoleMapping[demandManagementTask?.primaryusers];

                    // LoA approver with matching role
                    const loaApproverObj = approvers?.find(
                        (obj) => obj.approvalbucket === 'LoA Approval' && obj.role === loaRole
                    );

                    // -------------------------------------------
                    //  Get the Final Approvers
                    // -------------------------------------------

                    const approvalStatus = demandManagementTask?.status === 'Cancelled' ? 'Cancelled' : 'Not Started';

                    const allFinalApprovers = [

                        // Final BU Approver
                        {
                            'assignedto': {
                                'email': approvers?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === demandManagementTask?.businessunit)?.approveremail,
                                'givenname': approvers?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === demandManagementTask?.businessunit)?.approvergivenname,
                                'surname': approvers?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === demandManagementTask?.businessunit)?.approversurname
                            },
                            'tasktype': 'Final Approval',
                            'lastmodifieddate': '',
                            'status': approvalStatus,
                        },
                        // COO Approver
                        {
                            'assignedto': {
                                'email': approvers?.find((obj) => obj.approvalbucket === 'COO Approval')?.approveremail,
                                'givenname': approvers?.find((obj) => obj.approvalbucket === 'COO Approval')?.approvergivenname,
                                'surname': approvers?.find((obj) => obj.approvalbucket === 'COO Approval')?.approversurname,
                            },
                            'tasktype': 'COO Approval',
                            'lastmodifieddate': '',
                            'status': approvalStatus,
                        },
                        // LoA Approver
                        {
                            'assignedto': {
                                'email': loaApproverObj?.approveremail,
                                'givenname': loaApproverObj?.approvergivenname,
                                'surname': loaApproverObj?.approversurname,
                            },
                            'tasktype': 'LoA Approval',
                            'lastmodifieddate': '',
                            'status': approvalStatus,
                        },
                        // Finance Approver
                        {
                            'assignedto': {
                                'email': demandManagementTask?.financemanager?.email,
                                'givenname': demandManagementTask?.financemanager?.givenname,
                                'surname': demandManagementTask?.financemanager?.surname,
                            },
                            'tasktype': 'Finance Approval',
                            'lastmodifieddate': '',
                            'status': approvalStatus,
                        },
                    ];

                    setApprovalData({
                        'finalApprovals': [...document.filter((doc) => ['Finance Approval', 'LoA Approval', 'COO Approval', 'Final Approval'].includes(doc?.tasktype)), ...allFinalApprovers],
                    })

                })
                .catch((error) => {

                    console.log('error', error);

                    // Set error on toast
                    setToast({
                        'type': 'error',
                        'message': 'Failed to get first round approvals',
                    });

                })

        }

        function onError(error) {

            console.log('error', error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to get first and second round approvals',
            });

        }

        const unsubscribe = QueryListener('requests', [
            ['parentrequestid', '==', task?.parentrequestid],
        ], onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line
    }, [task])

    // Document Listener
    // - Get the agreement document if it has been published to Inventory
    useEffect(() => {

        if (task === undefined) return;
        if (task?.poanumber === undefined) return;

        function onLoadChange(document) {

            if (document?.length > 0) {
                setAgreementDetails(document[0]);
            }

        }

        function onError(error) {

            console.log('error', error);

        }

        const unsubscribe = QueryListener('agreements', [['poanumber', '==', task?.poanumber]], onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

    }, [task])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-2 p-0'>

            {/* ===================================================== */}
            {/*  Mandatory Approvals                                  */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'firstRoundApprovalsCollapsed': !taskSection.firstRoundApprovalsCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.firstRoundApprovalsCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Mandatory Approvals</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.firstRoundApprovalsCollapsed}></hr>

                {/* ------------- FIRST ROUND APPROVALS ------------- */}

                <div className='p-[3%]' hidden={taskSection?.firstRoundApprovalsCollapsed}>

                    {/*  Approvals Table */}
                    <div className='w-full overflow-x-auto border border-[#D8D8D8] rounded px-[1%]'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr className='text-[#424242]'>
                                    <th className='py-[15px] px-[5px] font-medium'></th>
                                    <th className='py-[15px] px-[10px] font-medium'>Type</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Approver</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Modified</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    //  No Approvals Found
                                    approvalData?.firstRoundApprovals?.length === 0 || approvalData?.firstRoundApprovals === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={5}>
                                                    <div className='text-center p-[2%]'>
                                                        No approvals found.
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        // Approvals Found
                                        (
                                            approvalData?.firstRoundApprovals?.map((item, index) => (
                                                <tr key={index}>

                                                    <td className='py-[10px] px-[5px] border-t border-t-[#D8D8D8]'></td>

                                                    {/* Type */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis'>
                                                            {item?.tasktype}
                                                        </div>
                                                    </td>

                                                    {/* Approver*/}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <p className='m-0 cursor-pointer'>{item?.assignedto?.email}</p>
                                                    </td>

                                                    {/* Last Modified Date */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {item?.lastmodifieddate === '' ? '-' : ConvertDate(item?.lastmodifieddate)}
                                                    </td>

                                                    {/* Status */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <StatusLabel status={item?.status} tasktype={item?.tasktype}></StatusLabel>
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Final Approvals                                      */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'finalApprovalsCollapsed': !taskSection.finalApprovalsCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.finalApprovalsCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Final Approvals</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.finalApprovalsCollapsed}></hr>

                {/* ------------- FINAL APPROVALS ------------- */}

                <div className='p-[3%]' hidden={taskSection?.finalApprovalsCollapsed}>

                    {/* Approvals Table */}
                    <div className='w-full overflow-x-auto border border-[#D8D8D8] rounded px-[1%]'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr className='text-[#424242]'>
                                    <th className='py-[15px] px-[5px] font-medium'></th>
                                    <th className='py-[15px] px-[10px] font-medium'>Role</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Assigned To</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Modified</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    //  Final Approvals not sent yet. Don't show Final Approvals if there are no quotes
                                    approvalData?.finalApprovals?.length === 0 || approvalData?.finalApprovals === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={5}>
                                                    <div className='text-center p-[2%]'>
                                                        Final approvers will be shown here when a quote is submitted for approval.
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        // Approvals Found
                                        (
                                            approvalData?.finalApprovals?.map((item, index) => (
                                                <tr key={index}>

                                                    <td className='py-[10px] px-[5px] border-t border-t-[#D8D8D8]'></td>

                                                    {/* Role */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis'>
                                                            {item?.tasktype}
                                                        </div>
                                                    </td>

                                                    {/* Assigned To*/}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            item?.assignedto?.email === 'Unassigned' ?
                                                                <label>Unassigned</label>
                                                                :
                                                                <div className='flex flex-col'>
                                                                    <label className='capitalize'>{item?.assignedto?.givenname} {item?.assignedto?.surname} </label>
                                                                    <label className='text-[14px] text-[#616161]'>{item?.assignedto?.email}</label>
                                                                </div>
                                                        }
                                                    </td>

                                                    {/* Last Modified Date */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {item?.lastmodifieddate === '' ? '-' : ConvertDate(item?.lastmodifieddate)}
                                                    </td>

                                                    {/* Status */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <StatusLabel status={item?.status} tasktype={item?.tasktype}></StatusLabel>
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Supplementary Approvals                              */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'secondRoundApprovalsCollapsed': !taskSection.secondRoundApprovalsCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.secondRoundApprovalsCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Supplementary Approvals</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.secondRoundApprovalsCollapsed}></hr>

                {/* ------------- SECOND ROUND APPROVALS ------------- */}

                <div className='p-[3%]' hidden={taskSection?.secondRoundApprovalsCollapsed}>

                    {/*  Approvals Table */}
                    <div className='w-full overflow-x-auto border border-[#D8D8D8] rounded px-[1%]'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr className='text-[#424242]'>
                                    <th className='py-[15px] px-[5px] font-medium'></th>
                                    <th className='py-[15px] px-[10px] font-medium'>Approver</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Subject</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Modified</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Status</th>
                                    <th className='py-[15px] px-[10px] font-medium'>
                                        <Tooltip
                                            message={'+ Create Approval'}
                                            children={
                                                (
                                                    task?.status === 'Completed' ||
                                                    task?.status === 'Cancelled' ||
                                                    task?.stage === 'Execute' ||
                                                    task?.stage === 'Raise PO'
                                                ) ?

                                                    // Disabled
                                                    <img className='min-w-[25px]' src={AddDisabled} alt='add'></img>
                                                    :

                                                    // Active
                                                    <img className='cursor-pointer min-w-[25px]' src={Add} alt='add' onClick={() => setAddSecondRoundApprovalPaneOpen(true)}></img>
                                            }
                                        ></Tooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    approvalData?.secondRoundApprovals?.length === 0 || approvalData?.secondRoundApprovals === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={6}>
                                                    <div className='text-center p-[2%]'>
                                                        No approvals found.
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            approvalData?.secondRoundApprovals?.map((item, index) => (
                                                <tr key={index} className='hover:bg-[#F0F7F7]'>

                                                    <td className='py-[10px] px-[5px] border-t border-t-[#D8D8D8]'></td>

                                                    {/* Approver Email */}
                                                    <td className='flex flex-col py-[10px] px-[10px] border-t border-t-[#D8D8D8]' onClick={() => {
                                                        setViewPaneApprover(item)
                                                    }}>
                                                        <p className='m-0 cursor-pointer hover:underline hover:font-medium'>{item?.assignedto?.email}</p>
                                                        <p className='text-[13px] m-0 text-slate-600'>{item?.approverrole}</p>
                                                    </td>

                                                    {/* Subject */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis'>
                                                            {item?.emailsubject}
                                                        </div>
                                                    </td>

                                                    {/* Last Modified Date */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>{ConvertDate(item?.lastmodifieddate)}</td>

                                                    {/* Status */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <StatusLabel status={item?.status} tasktype={item?.tasktype}></StatusLabel>
                                                    </td>

                                                    {/* Cancel */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            item?.status !== 'In Progress' ?
                                                                <img className='cursor-not-allowed' src={CancelDisabled} alt='cancel'></img>
                                                                :
                                                                <div
                                                                    className='Cancel-Icon min-w-[30px]'
                                                                    onClick={() => {

                                                                        setCancelModalApprover(true);
                                                                        setCancelApprovalData(item);

                                                                    }}
                                                                ></div>
                                                        }
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Quote                                                */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 justify-between px-[2%] py-[18px] cursor-pointer w-full' onClick={() => setTaskSection({ 'quotesCollapsed': !taskSection.quotesCollapsed })}>
                    
                    <div className='flex flex-row items-center gap-3'>
                        <img
                            className='cursor-pointer w-fit'
                            src={taskSection?.quotesCollapsed ? Collapse : Expand}
                            alt={'>'}
                        ></img>

                        <h4 className='text-[20px] m-0'>Quote</h4>
                    </div>

                    {/* Status Label */}
                    {
                        [
                            {
                                'status': 'For Review',
                                'label':'Quote Review',
                                'background': '#FBF6D9',
                                'color': '#DC9A03'
                            },
                            {
                                'status': 'Completed',
                                'label': 'Quote Approved',
                                'background': '#E7F2DA',
                                'color': '#237B4B'
                            },
                            {
                                'status': 'Rejected',
                                'label': 'Quote Rejected',
                                'background': '#FCF4F6',
                                'color': '#C4314B'
                            },
                        ]?.filter((style) => style.status === approvalData?.currentQuoteReviewTask?.status)
                        .map((object, index) => (
                            <label key={index} style={{ backgroundColor: object.background, color: object.color, border: `1px solid ${object.color}` }} className='px-3 py-1 rounded-[30px] font-medium text-[14px]' hidden={approvalData?.currentQuoteReviewTask?.status === undefined}>
                                {object.label}
                            </label>
                        ))
                    }

                </div>
                <hr className='m-0' hidden={taskSection?.quotesCollapsed}></hr>

                {/* ------------- QUOTE ------------- */}

                <div className='p-[3%] flex flex-col gap-3' hidden={taskSection?.quotesCollapsed}>

                    {/* Status Banner */}
                    {
                        [
                            {
                                'status': 'For Review',
                                'description': 'provided',
                                'background': '#FBF6D9',
                                'color': '#DC9A03'
                            },
                            {
                                'status': 'Completed',
                                'description': 'approved',
                                'background': '#E7F2DA',
                                'color': '#237B4B'
                            },
                            {
                                'status': 'Rejected',
                                'description': 'rejected',
                                'background': '#FCF4F6',
                                'color': '#C4314B'
                            },
                        ]?.filter((style) => style.status === approvalData?.currentQuoteReviewTask?.status)
                        .map((object, index) => (
    
                            <div key={index} className='text-[#535353] leading-7 py-3 px-4' style={{ backgroundColor: object.background, borderLeft: `5px solid ${object.color}` }} hidden={approvalData?.currentQuoteReviewTask?.status === undefined}>
                                <>
                                    <strong className='capitalize'>{approvalData?.currentQuoteReviewTask?.lastmodifiedby?.givenname} {approvalData?.currentQuoteReviewTask?.lastmodifiedby?.surname}</strong> has {object.description} this quote on {ConvertDate(approvalData?.currentQuoteReviewTask?.lastmodifieddate)}.
                                </>
                                <br></br>
                                Comment: {approvalData?.currentQuoteReviewTask?.status === 'Rejected' ? approvalData?.currentQuoteReviewTask?.rejectionreason : approvalData?.currentQuoteReviewTask?.comment?.length > 0 ? approvalData?.currentQuoteReviewTask?.comment : 'N/A'}
                            </div>
                        ))
                    }

                    {/* Quote Table */}
                    <div className='border border-[#D8D8D8] rounded'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr className='text-[#424242]'>
                                    <th className='py-[15px] px-[20px] font-medium'>Description</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Quantity</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Unit Price (exTax)</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Total (exTax)</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Currency</th>
                                    <th className='py-[15px] px-[10px] font-medium'>
                                        <Tooltip
                                            message={'+ Add Cost'}
                                            children={
                                                (
                                                    task?.status === 'Completed' ||
                                                    task?.status === 'Cancelled' ||
                                                    approvalData?.quoteReviewTasks?.some(quote => quote.status === 'Completed' || quote?.status === 'For Review') ||
                                                    task?.stage === 'Execute' ||
                                                    task?.stage === 'Raise PO' ||
                                                    task?.requesttype === 'Establish NDA'
                                                ) 
                                                ?
                                                // Disabled
                                                <img className='min-w-[25px]' src={AddDisabled} alt='add'></img>

                                                :
                                                // Active
                                                <img className='cursor-pointer min-w-[25px]' src={Add} alt='add' onClick={() => setAddQuotePaneOpen(true)}></img>
                                            }
                                        ></Tooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    approvalData?.currentQuoteReviewTask?.quote?.length === 0 || approvalData?.currentQuoteReviewTask?.quote === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={6}>
                                                    <div className='text-center p-[2%]'>
                                                        Click on the <b>+</b> to add costs
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            approvalData?.currentQuoteReviewTask?.quote?.map((item, index) => (
                                                <tr key={index} className='hover:bg-[#F0F7F7]'>

                                                    {/* Description */}
                                                    <td className='py-[10px] px-[20px] border-t border-t-[#D8D8D8]'>
                                                        <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer'>
                                                            {item?.description?.length > 0 ? item?.description : '-'}
                                                        </div>
                                                    </td>

                                                    {/* Quantity */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                        {item?.quantity}
                                                    </td>

                                                    {/* Unit Price (exTax) */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                        {item?.unitprice}
                                                    </td>

                                                    {/* Total (exTax) */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                        {item?.total}
                                                    </td>

                                                    {/* Currency */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                        {item?.currency}
                                                    </td>

                                                    {/* Kebab Menu */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <KebabDropdown 
                                                            disabled={
                                                                (approvalData?.currentQuoteReviewTask?.status !== 'Draft' && approvalData?.currentQuoteReviewTask?.status !== 'Rejected') || 
                                                                task?.status === 'Completed' ||
                                                                task?.status === 'Cancelled' || 
                                                                task?.stage === 'Execute' ||
                                                                task?.stage === 'Raise PO'
                                                            }
                                                        >
                                                            <div className=' bg-white  rounded shadow-lg z-10'>
                                                                <button
                                                                    onClick={() => handleQuoteLineItemEdit(index)}
                                                                    className='block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100'
                                                                    disabled={
                                                                        (approvalData?.currentQuoteReviewTask?.status !== 'Draft' && approvalData?.currentQuoteReviewTask?.status !== 'Rejected') ||
                                                                        task?.status === 'Completed' ||
                                                                        task?.status === 'Cancelled' || 
                                                                        task?.stage === 'Execute' ||
                                                                        task?.stage === 'Raise PO'                                                                    
                                                                    }
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleQuoteLineItemDelete(index, item?.total)}
                                                                    className='block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100'
                                                                    disabled={
                                                                        (approvalData?.currentQuoteReviewTask?.status !== 'Draft' && approvalData?.currentQuoteReviewTask?.status !== 'Rejected') || 
                                                                        task?.status === 'Completed' ||
                                                                        task?.status === 'Cancelled' || 
                                                                        task?.stage === 'Execute' ||
                                                                        task?.stage === 'Raise PO'                                                                     
                                                                    }
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </KebabDropdown>
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Footer */}
                    <div className='flex flex-col gap-2 justify-end w-full'>
            
                        {/* Quote Total */}
                        <label className='text-end mb-1' hidden={task?.agreement?.totalagreementamount === '' || task?.agreement?.totalagreementamount === undefined}>
                            Total Quote Value (exTax): <b>{(task?.agreement?.totalagreementamount === '' || task?.agreement?.totalagreementamount === undefined) ? 0 : Number(task?.agreement?.totalagreementamount)?.toFixed(2)?.toLocaleString()} {task?.agreement?.currency}</b>
                        </label>

                        {/* Attached Files */}
                        <div className='flex flex-row flex-wrap gap-2 w-full' hidden={approvalData?.currentQuoteReviewTask?.attachedfiles?.length === 0}>
                            {
                                approvalData?.currentQuoteReviewTask?.attachedfiles?.map((file, index) => (
                                    <div className='w-fit flex flex-row gap-2 bg-[#F5F5F5] px-3 py-2 rounded-md truncate' key={index}>
                                        <img src={File} alt='file'></img>
                                        <a className='text-[#0972D3] no-underline font-medium' href={file?.fileUrl} target='_blank' rel='noreferrer'>
                                            {file?.fileName}
                                        </a>
                                    </div>
                                ))
                            }
                        </div>

                        {/* Submit for Review Button */}
                        <div className='w-full flex flex-row justify-end'>
                            {/* NH TODO - Add disabled state when there are no changes when the quote is rejected */}
                            <button 
                                className='Primary-Button w-fit' 
                                onClick={() => setSubmitForReviewPaneOpen(true)} 
                                disabled={
                                    task?.status === 'Completed' ||
                                    task?.status === 'Cancelled' ||
                                    approvalData?.quoteReviewTasks?.some(quote => quote.status === 'Completed' || quote?.status === 'For Review') ||
                                    approvalData?.currentQuoteReviewTask?.status === 'Rejected' ||
                                    task?.stage === 'Execute' ||
                                    task?.stage === 'Raise PO' ||
                                    approvalData?.currentQuoteReviewTask?.quote?.length === 0
                                }
                            >
                                Submit for Review
                            </button>
                        </div>

                        {/* Label */}
                        <label className='font-light text-[15px] text-end'>
                            {
                                approvalData?.currentQuoteReviewTask?.status === 'Rejected' ?
                                <i>This quote has been rejected by the request owner. Please note that editing the quote will reset the Quote table and remove all files attached to the original submission. A new submission will required for the request owner's review.</i>
                                :
                                <i>Total Quote Value will be submitted to the request owner for review. If approved, the final approvers will be notified for further action.</i>
                            }
                        </label>

                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Agreement Information                                */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'agreementInformationCollapsed': !taskSection.agreementInformationCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.agreementInformationCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Agreement Information</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.agreementInformationCollapsed}></hr>

                {/* ------------- AGREEMENT INFORMATION ------------- */}

                <div className='px-[3%] py-[2%]' hidden={taskSection?.agreementInformationCollapsed}>

                    <>

                        {/* Info Label */}
                        <div className='flex flex-row gap-3 px-[15px] py-[12px] text-[15px] rounded-md mx-1 my-1 bg-[#E9F7FF] border-1 border-[#0972D3]' hidden={agreementDetails === undefined}>
                            <img src={Info} alt='info'></img>
                            <p className='mb-0 leading-6'>
                                <b className='font-medium'>{agreementDetails?.createdby?.givenname} {agreementDetails?.createdby?.surname}</b> has added this agreement into Inventory on {ConvertDate(agreementDetails?.createddate)}.
                                <br></br>
                                <a
                                    className='flex flex-row gap-2 items-center '
                                    href={
                                        process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/agreement/${agreementDetails?.agreementid}?view=overview`
                                            : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/agreement/${agreementDetails?.agreementid}?view=overview`
                                                : `https://inventory.digitalservices.lendlease.com/inventory/agreement/${agreementDetails?.agreementid}?view=overview`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    Make changes to agreement in Inventory
                                    <img src={ExternalLink} alt='link'></img>
                                </a>
                            </p>
                        </div>

                        {/* 1. PoA Number */}
                        <div className='FormComponent-Row'>
                            <label className='font-medium'>1. PoA Number </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '200px' }}
                                type='text'
                                value={task?.poanumber}
                                disabled
                            ></input>
                        </div>

                        {/* 2. Agreement Type  */}
                        <LiveField
                            type={'choice'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.agreementtype'}
                            defaultValue={task?.agreement?.agreementtype === undefined ? task?.agreementtype : task?.agreement?.agreementtype}
                            label={<label className='font-medium'> 2. Agreement Type <span className='text-[#C4314B]'>*</span></label>}
                            options={[
                                'Consultancy Services Agreement (CSA)',
                                'Hardware Order',
                                'Letter of Intent (LoI)',
                                'Master Services Agreement (MSA)',
                                'Non-Disclosure Agreement (NDA)',
                                'Novation/Assignment Agreement',
                                'Service Order (SO)',
                                'Software License',
                                'Subscription',
                                'Supply Agreement',
                                'Variation'
                            ]}
                            styleInput={{ width: '400px' }}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                        ></LiveField>

                        {/* 3. Vendor Name  */}
                        <div className='FormComponent-Row'>
                            <label className='font-medium'>3. Vendor Name <span className='text-[#C4314B]'>*</span> </label>

                            <LiveField
                                type={'lookup'}
                                collectionid={'requests'}
                                documentid={task?.requestid}
                                fieldpath={'agreement.vendor'}
                                defaultValue={task?.agreement?.vendor?.vendorname === undefined || task?.agreement?.vendor?.vendorname === "" ? task?.vendor?.vendorname : task?.agreement?.vendor?.vendorname}
                                placeholder={'Search vendor by name'}
                                dataFieldCollectionid={'vendors'}
                                dataFieldPrimary={'vendorname'}
                                dataFieldSecondary={'tradingas'}
                                dataFieldTertiary={'vendorid'}
                                disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                            ></LiveField>

                            <div className='w-[450px] flex flex-row gap-2 px-[15px] py-[7px] text-[15px] rounded-md mx-1 my-1 bg-[#E9F7FF] border-1 border-[#0972D3]'>
                                <img src={Info} alt='info'></img>
                                <p className='mb-0'>
                                    To add a new vendor, click <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? 'https://devinventory.digitalservices.lendlease.com/inventory?view=vendors' : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? 'https://uatinventory.digitalservices.lendlease.com/inventory?view=vendors' : 'https://inventory.digitalservices.lendlease.com/inventory?view=vendors'} target='_blank' rel='noopener noreferrer'>here</a>.
                                </p>
                            </div>

                            {/* New Vendor Request Form */}
                            <div className='flex flex-col justify-center bg-[#E7F0F4] px-4 py-3 rounded-md' hidden={task?.newvendorname === undefined || task?.newvendorname?.length === 0}>

                                {/* Header */}
                                <div className='flex flex-row gap-2 items-center'>
                                    <img src={InfoRequired} alt='info'></img>
                                    <label className='font-medium text-[#5E93AC]'>New Vendor Request</label>
                                </div>

                                {/* Form */}
                                <div className='grid grid-cols-2 gap-2 mb-2'>

                                    {/* Column 1 */}
                                    <div className='flex flex-col gap-2 px-2 mt-0 mb-2'>

                                        {/* Vendor Name */}
                                        <div className='Task-Form-Row'>
                                            <label className='font-medium'> Vendor Name </label>
                                            <input
                                                className='Input-Field-Text'
                                                style={{ width: '100%' }}
                                                value={task?.newvendorname}
                                                type='text'
                                                disabled
                                            ></input>
                                        </div>

                                        {/* Vendor Phone */}
                                        <div className='Task-Form-Row'>
                                            <label className='font-medium'> Vendor Phone </label>
                                            <input
                                                className='Input-Field-Text'
                                                style={{ width: '100%' }}
                                                value={task?.newvendorcontactphone}
                                                type='text'
                                                disabled
                                            ></input>
                                        </div>
                                    </div>

                                    {/* Column 2 */}
                                    <div className='flex flex-col gap-2 px-2 mt-0 mb-2'>
                                        {/* Vendor Trading As Name */}
                                        <div className='Task-Form-Row'>
                                            <label className='font-medium'> Vendor Trading As Name </label>
                                            <input
                                                className='Input-Field-Text'
                                                style={{ width: '100%' }}
                                                value={task?.newvendortradingname}
                                                type='text'
                                                disabled
                                            ></input>
                                        </div>
                                        {/* Vendor Email */}
                                        <div className='Task-Form-Row'>
                                            <label className='font-medium'> Vendor Email </label>
                                            <input
                                                className='Input-Field-Text'
                                                style={{ width: '100%' }}
                                                value={task?.newvendorcontactemail}
                                                type='text'
                                                disabled
                                            ></input>
                                        </div>
                                    </div>

                                </div>

                            </div>

                        </div>

                        {/* 4. Execution Method */}
                        <LiveField
                            type={'choice'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.executionmethod'}
                            defaultValue={task?.agreement?.executionmethod}
                            label={<label className='font-medium'> 4. Execution Method <span className='text-[#C4314B]'>*</span></label>}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                            options={[
                                'PoA',
                                's127',
                                'None',
                                'Other'
                            ]}
                        ></LiveField>

                        {/* 5. Lendlease Entity */}
                        <LiveField
                            type={'choice'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.lendleaseentity'}
                            defaultValue={task?.agreement?.lendleaseentity}
                            styleInput={{ width: '450px' }}
                            label={<label className='font-medium'> 5. Lendlease Entity <span className='text-[#C4314B]'>*</span></label>}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                            options={[
                                'TBC',
                                'Lendlease Asia Holdings Pte. Ltd',
                                'Lendlease Building Pty Ltd',
                                'Lendlease Chilled Water (Barangaroo South) Pty Limited',
                                'Lendlease Construction Pty Limited',
                                'Lendlease Design Build',
                                'Lendlease Development Pty Ltd',
                                'Lendlease Digital Asia Pte Ltd',
                                'Lendlease Digital Australia Holdings Pty Limited',
                                'Lendlease Digital Australia Pty Ltd',
                                'Lendlease Digital Investments Pty Limited',
                                'Lendlease Digital US Holdings LLC',
                                'Lendlease Digital US LLC',
                                'Lendlease Embedded Network (Barangaroo South) Pty Limited',
                                'Lendlease Engineering Pty Ltd',
                                'Lendlease Europe Limited',
                                'Lendlease Europe Limited',
                                'Lendlease Global Commercial Trust Management Pte Ltd',
                                'Lendlease Group Services Pty Limited',
                                'Lendlease ICT Services, Inc',
                                'Lendlease Management Services Limited',
                                'Lendlease Project Management & Construction (Shanghai) Co Ltd',
                                'Lendlease Recycled Water (Barangaroo South) Pty Limited',
                                'Lendlease Retirement Living Holding Pty Limited',
                                'Lendlease Services Pty Limited',
                                'Lendlease Singapore Pte Ltd',
                                'Lendlease Technology Delivery Pty Limited',
                                'Lendlease Technology Pty Ltd',
                                'Podium Asset Holdings Pty Limited',
                                'Podium Property Services LLC',
                                'Lendlease (Elephant & Castle) Ltd',
                                'Lendlease (US) Construction Inc',
                                'Lendlease (US) Construction, LMB Inc',
                                'Lendlease (US) Development',
                                'Lendlease (US) Partnerships',
                                'Lendlease (US) Services Inc',
                            ]}
                        ></LiveField>

                        {/* 6. Parent Contract  */}
                        <LiveField
                            type={'lookup'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.parentcontract'}
                            defaultValue={task?.agreement?.parentcontract?.poanumber}
                            placeholder={'Search parent contract by PoA number'}
                            dataFieldCollectionid={'agreements'}
                            dataFieldPrimary={'poanumber'}
                            dataFieldSecondary={'vendor.vendorname'}
                            label={<label className='font-medium'> 6. Parent Contract</label>}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                        ></LiveField>

                        {/* 7. Agreement Start Date */}
                        <LiveField
                            type={'date-string'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.agreementstartdate'}
                            defaultValue={task?.agreement?.agreementstartdate}
                            label={<label className='font-medium'> 7. Agreement Start Date  <span className='text-[#C4314B]'>*</span></label>}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                        ></LiveField>

                        {/* 8. Agreement End Date */}
                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 8. Agreement End Date <span className='text-[#C4314B]'>*</span></label>

                            {/* Checkbox to disable the date */}
                            <div className='flex flex-row items-start px-[5px]'>
                                <label className='Checkbox-Container' style={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO') ? { cursor: 'not-allowed' } : { cursor: 'auto' }}>
                                    <input
                                        required
                                        type='checkbox'
                                        name='confirmation'
                                        checked={noEndDate}
                                        onChange={(e) => handleAgreementEndDateChange(e.target.checked)}
                                        disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                    ></input>
                                    <span className='Checkbox'></span>
                                </label>

                                <label className='max-w-[400px]'>
                                Agreement has no end date
                                </label>
                            </div>

                            {!noEndDate && <LiveField
                                type={'date-string'}
                                collectionid={'requests'}
                                documentid={task?.requestid}
                                fieldpath={'agreement.agreementenddate'}
                                defaultValue={task?.agreement?.agreementenddate}
                                disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                validation={{
                                    type: 'date',
                                    minDate: task?.agreement?.agreementstartdate
                                }}
                            ></LiveField>}
                        </div>

                        {/* 9. Currency */}
                        <LiveField
                            type={'choice'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.currency'}
                            defaultValue={task?.agreement?.currency === undefined || task?.agreement?.currency === "" ? task?.currency : task?.agreement?.currency}
                            label={<label className='font-medium'> 9. Currency</label>}
                            disabled={true}
                            options={[
                                'AUD',
                                'GBP',
                                'SGD',
                                'USD',
                                'EUR',
                                'EUR',
                                'MYR',
                                'N/A',
                            ]}
                        ></LiveField>

                        {/* 10. Total Agreement Amount  */}
                        <LiveField
                            type={'text'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.totalagreementamount'}
                            defaultValue={task?.agreement?.totalagreementamount === undefined ? '' : Number(task?.agreement?.totalagreementamount).toLocaleString()}
                            placeholder={'Submit a Quote to get a Total Agreement Amount'}
                            label={<label className='font-medium'> 10. Total Agreement Amount</label>}
                            disabled={true}
                        ></LiveField>

                        {/* 11. Auto Renewals  */}
                        <LiveField
                            type={'choice'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.autorenewals'}
                            defaultValue={task?.agreement?.autorenewals}
                            label={<label className='font-medium'> 11. Auto Renewals <span className='text-[#C4314B]'>*</span></label>}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO' || task?.requesttype === 'Establish NDA' || task?.requesttype === 'Assign/Novate Agreement' || task?.requesttype === 'Terminate Existing Agreement')}
                            options={[
                                'Yes',
                                'No',
                            ]}
                        ></LiveField>

                        {/* 11a. Notice period to terminate auto renewal? */}
                        {
                            task?.agreement?.autorenewals === 'Yes' &&
                            <div className='gap-[5px] p-[5px]'>

                                <label className='font-medium pt-[5px]'>11a. Notice period to terminate auto renewal?  <span className='text-[#C4314B]'>*</span></label>
                                <div className='flex flex-row pt-[10px]'>

                                    <LiveField
                                        type={'number'}
                                        collectionid={'requests'}
                                        documentid={task?.requestid}
                                        fieldpath={'agreement.noticeperiodforautorenewaltermination.term'}
                                        defaultValue={task?.agreement?.noticeperiodforautorenewaltermination?.term}
                                        disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                        styleInput={{ width: '150px' }}
                                        placeholder={'Enter value'}
                                    ></LiveField>

                                    <LiveField
                                        type={'choice'}
                                        collectionid={'requests'}
                                        documentid={task?.requestid}
                                        fieldpath={'agreement.noticeperiodforautorenewaltermination.period'}
                                        defaultValue={task?.agreement?.noticeperiodforautorenewaltermination?.period}
                                        disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                        options={[
                                            'Days',
                                            'Months',
                                            'Years',
                                        ]}
                                        styleInput={{ width: '200px' }}
                                    ></LiveField>

                                </div>
                            </div>
                        }

                        {/* 12. Extension Option  */}
                        <LiveField
                            type={'choice'}
                            collectionid={'requests'}
                            documentid={task?.requestid}
                            fieldpath={'agreement.extensionagreement'}
                            defaultValue={task?.agreement?.extensionagreement}
                            label={<label className='font-medium'> 12. Extension Option <span className='text-[#C4314B]'>*</span></label>}
                            disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO' || task?.requesttype === 'Establish NDA' || task?.requesttype === 'Assign/Novate Agreement' || task?.requesttype === 'Terminate Existing Agreement')}
                            options={[
                                'Yes',
                                'No',
                            ]}
                        ></LiveField>

                        {/* 12a. Notice period to extend agreement? */}
                        {
                            task?.agreement?.extensionagreement === 'Yes' &&
                            <div className='gap-[5px] p-[5px]'>

                                <label className='font-medium pt-[5px]'>12a. Notice period to extend agreement?  <span className='text-[#C4314B]'>*</span></label>
                                <div className='flex flex-row pt-[10px]'>

                                    <LiveField
                                        type={'number'}
                                        collectionid={'requests'}
                                        documentid={task?.requestid}
                                        fieldpath={'agreement.noticeperiodforextension.term'}
                                        defaultValue={task?.agreement?.noticeperiodforextension?.term}
                                        disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                        styleInput={{ width: '150px' }}
                                        placeholder={'Enter value'}
                                    ></LiveField>

                                    <LiveField
                                        type={'choice'}
                                        collectionid={'requests'}
                                        documentid={task?.requestid}
                                        fieldpath={'agreement.noticeperiodforextension.period'}
                                        defaultValue={task?.agreement?.noticeperiodforextension?.period}
                                        disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                        options={[
                                            'Days',
                                            'Months',
                                            'Years',
                                        ]}
                                        styleInput={{ width: '200px' }}
                                    ></LiveField>

                                </div>
                            </div>
                        }

                        {/* =========================================================== */}
                        {/*  13. Document Storage Link                                  */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <LiveField
                                styleInput={{ width: '100%' }}
                                type={'text'}
                                collectionid={'requests'}
                                documentid={task?.requestid}
                                fieldpath={'agreement.documentstoragelink'}
                                label={<label className='font-medium'> 13. Document Storage Link <span className='text-[#C4314B]'>*</span></label>}
                                defaultValue={task?.agreement?.documentstoragelink}
                                placeholder={'https://'}
                                disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                                validation={{
                                    type: 'link'
                                }}
                            ></LiveField>
                        </div>

                        {/* =========================================================== */}
                        {/*  14. Negotiated Hard Savings                                */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <LiveField
                                type={'text'}
                                collectionid={'requests'}
                                documentid={task?.requestid}
                                fieldpath={'agreement.hardsavings'}
                                label={<label className='font-medium'> 14. Negotiated Hard Savings <span className='text-[#C4314B]'>*</span></label>}
                                defaultValue={task?.agreement?.hardsavings}
                                placeholder={'Enter amount'}
                                disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO' || task?.requesttype === 'Establish NDA' || task?.requesttype === 'Assign/Novate Agreement' || task?.requesttype === 'Terminate Existing Agreement')}
                            ></LiveField>
                        </div>

                        {/* =========================================================== */}
                        {/*  15. Negotiated Soft Savings                                */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <LiveField
                                type={'text'}
                                collectionid={'requests'}
                                documentid={task?.requestid}
                                fieldpath={'agreement.softsavings'}
                                label={<label className='font-medium'> 15. Negotiated Soft Savings <span className='text-[#C4314B]'>*</span></label>}
                                defaultValue={task?.agreement?.softsavings}
                                placeholder={'Enter amount'}
                                disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO' || task?.requesttype === 'Establish NDA' || task?.requesttype === 'Assign/Novate Agreement' || task?.requesttype === 'Terminate Existing Agreement')}
                            ></LiveField>
                        </div>

                    </>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Comments                                             */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'commentsCollapsed': !taskSection.commentsCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.commentsCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Comments</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.commentsCollapsed}></hr>

                {/* ------------- COMMENTS ------------- */}

                <div className='p-[3%]' hidden={taskSection?.commentsCollapsed}>

                    <CommentsThread
                        task={task}
                    ></CommentsThread>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Execution Not Required Checkbox                      */}
            {/* ===================================================== */}

            <div className='flex flex-row items-start gap-2 my-3'>
                <label className='Checkbox-Container' style={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO') ? { cursor: 'not-allowed' } : { cursor: 'auto' }}>
                    <input
                        required
                        type='checkbox'
                        name='confirmation'
                        checked={isExecutionNotRequired}
                        onChange={() => handleExecutionNotRequired(!isExecutionNotRequired)}
                        disabled={(task?.status === 'Completed' || task?.status === 'Cancelled' || task?.stage === 'Execute' || task?.stage === 'Raise PO')}
                    ></input>
                    <span className='Checkbox'></span>
                </label>

                <label className='max-w-[400px]'>
                    Execution not required
                </label>
            </div>

            {/* ===================================================== */}
            {/*  Proceed to Next Stage Button                         */}
            {/* ===================================================== */}

            <div className='flex flex-col gap-1 mt-4 mb-0'>

                <button className='Primary-Button w-fit' onClick={() => moveToNextStage()} disabled={allowProceedToNextStageButton().length > 0}>

                    {/* Proceed to Next Stage */}
                    {
                        allowProceedToNextStageButton().length === 0 ?
                            'Proceed to Next Stage' :
                            <Tooltip
                                message={
                                    <ul className='list-disc absolute rounded text-left top-0 left-0 py-2 pr-2 bg-gray-800'>
                                        {allowProceedToNextStageButton().map((blocker, index) => (
                                            <li key={index}>{blocker}</li>
                                        ))}
                                    </ul>}
                                children={'Proceed to Next Stage'}
                            ></Tooltip>
                    }

                </button>

                <p className='font-light italic' hidden={task?.requesttype === 'New Initiative or Technology'}>
                    All approvals and quotes must be approved before proceeding to the next stage. Additionally, the agreement information needs to be completed, as this agreement record will be added to Inventory.
                </p>

            </div>
            {/* ===================================================== */}
            {/*  Add Approver Pane                                    */}
            {/* ===================================================== */}

            <AddSecondRoundApprovalPane
                task={task}
                addSecondRoundApprovalPaneOpen={addSecondRoundApprovalPaneOpen}
                setAddSecondRoundApprovalPaneOpen={setAddSecondRoundApprovalPaneOpen}
            ></AddSecondRoundApprovalPane>

            {/* ===================================================== */}
            {/*  View Approver Pane                                   */}
            {/* ===================================================== */}

            <ViewApproverPane
                task={task}
                viewPaneApprover={viewPaneApprover}
                setViewPaneApprover={setViewPaneApprover}
            ></ViewApproverPane>

            {/* ===================================================== */}
            {/*  Add Quote                                            */}
            {/* ===================================================== */}

            <AddQuotePane
                paneOpen={addQuotePaneOpen}
                setPaneOpen={setAddQuotePaneOpen}
                task={task}
                currentQuoteReviewTask={approvalData?.currentQuoteReviewTask}
            ></AddQuotePane>

            {/* ===================================================== */}
            {/*  Edit Quote                                           */}
            {/* ===================================================== */}

            <EditQuotePane
                paneOpen={editQuotePaneOpen}
                setPaneOpen={setEditQuotePaneOpen}
                task={task}
                currentQuoteReviewTask={approvalData?.currentQuoteReviewTask}
                quoteLineItemIndex={selectedQuoteLineItemIndex}
            ></EditQuotePane>

            {/* ===================================================== */}
            {/*  Submit Quote For Review                              */}
            {/* ===================================================== */}

            <SubmitQuoteForReviewPane
                paneOpen={submitForReviewPaneOpen}
                setPaneOpen={setSubmitForReviewPaneOpen}
                task={task}
                currentQuoteReviewTask={approvalData?.currentQuoteReviewTask}
            ></SubmitQuoteForReviewPane>

            {/* ===================================================== */}
            {/*  Cancel Approval Modal                                */}
            {/* ===================================================== */}

            <CancelApprovalModal
                cancelRequestModal={cancelModalApprover}
                setCancelRequestModal={setCancelModalApprover}
                task={cancelApprovalData}
            ></CancelApprovalModal>

        </div >
    )

}
